import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Select,
  Switch,
  InputNumber,
  Upload,
  Tag,
  Space,
} from 'antd';
import { UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { createTemplate, updateTemplate, getTemplate, uploadImage } from '../api/templates';
import type { UploadFile } from 'antd/es/upload/interface';

const { TextArea } = Input;
const { Option } = Select;

const TemplateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  // 图片状态
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [sourceImageUrl, setSourceImageUrl] = useState<string>('');
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [uploadingSource, setUploadingSource] = useState(false);

  // 加载现有模板数据
  useEffect(() => {
    if (isEdit && id) {
      loadTemplate(id);
    }
  }, [id, isEdit]);

  const loadTemplate = async (templateId: string) => {
    setLoading(true);
    try {
      const template = await getTemplate(templateId);
      form.setFieldsValue({
        title: template.title,
        prompt: template.prompt,
        category: template.category,
        targetAudience: template.targetAudience || [],
        tags: template.tags || [],
        isActive: template.isActive,
        sortOrder: template.sortOrder,
      });
      setPreviewImageUrl(template.previewImage);
      setSourceImageUrl(template.sourceImage || '');
    } catch (error: any) {
      message.error('加载失败: ' + (error.message || '未知错误'));
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  // 上传预览图
  const handleUploadPreview = async (file: File) => {
    console.log('📤 [Upload] 开始上传预览图:', file.name, file.size, 'bytes');
    setUploadingPreview(true);
    try {
      const url = await uploadImage(file);
      console.log('✅ [Upload] 预览图上传成功:', url);
      setPreviewImageUrl(url);
      message.success('预览图上传成功');
    } catch (error: any) {
      console.error('❌ [Upload] 预览图上传失败:', error);
      message.error('上传失败: ' + (error.response?.data?.errors?.[0]?.message || error.message || '未知错误'));
    } finally {
      setUploadingPreview(false);
    }
    return false; // 阻止默认上传行为
  };

  // 上传原图
  const handleUploadSource = async (file: File) => {
    console.log('📤 [Upload] 开始上传原图:', file.name, file.size, 'bytes');
    setUploadingSource(true);
    try {
      const url = await uploadImage(file);
      console.log('✅ [Upload] 原图上传成功:', url);
      setSourceImageUrl(url);
      message.success('原图上传成功');
    } catch (error: any) {
      console.error('❌ [Upload] 原图上传失败:', error);
      message.error('上传失败: ' + (error.response?.data?.errors?.[0]?.message || error.message || '未知错误'));
    } finally {
      setUploadingSource(false);
    }
    return false;
  };

  const handleSubmit = async (values: any) => {
    console.log('========== 提交表单 ==========');
    console.log('表单值:', values);
    console.log('预览图URL:', previewImageUrl);
    console.log('原图URL:', sourceImageUrl);

    // 验证必填字段
    if (!previewImageUrl) {
      message.warning('请上传预览图');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        title: values.title,
        prompt: values.prompt,
        category: values.category,
        previewImage: previewImageUrl,
        sourceImage: sourceImageUrl || undefined,
        targetAudience: values.targetAudience || [],
        tags: values.tags || [],
        isActive: values.isActive !== undefined ? values.isActive : true,
        sortOrder: values.sortOrder || 0,
      };

      console.log('准备发送的数据:', data);

      if (isEdit && id) {
        console.log('执行更新操作...');
        console.log('更新 ID:', id);
        const result = await updateTemplate(id, data);
        console.log('更新结果:', result);
        message.success('更新成功');
      } else {
        console.log('执行创建操作...');
        const result = await createTemplate(data);
        console.log('创建结果:', result);
        message.success('创建成功');
      }

      console.log('✅ 操作成功，准备跳转到列表页...');
      navigate('/templates');
      console.log('✅ 跳转命令已执行');
    } catch (error: any) {
      console.error('========== 保存失败 ==========');
      console.error('错误对象:', error);
      console.error('错误消息:', error.message);
      console.error('错误响应:', error.response?.data);
      message.error('保存失败: ' + (error.message || '未知错误'));
    } finally {
      setSubmitting(false);
      console.log('========== 提交完成 ==========');
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/templates')}
        style={{ marginBottom: '16px' }}
      >
        <span className="btn-text">返回列表</span>
      </Button>

      <Card title={isEdit ? '编辑模板' : '创建模板'} loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            sortOrder: 0,
            targetAudience: [],
            tags: [],
          }}
        >
          {/* 基本信息 */}
          <Form.Item
            label="标题"
            name="title"
            rules={[
              { required: true, message: '请输入标题' },
              { max: 200, message: '标题不能超过200字符' },
            ]}
          >
            <Input placeholder="例如：万圣节南瓜头" />
          </Form.Item>

          <Form.Item
            label="提示词"
            name="prompt"
            rules={[{ required: true, message: '请输入提示词' }]}
          >
            <TextArea
              rows={6}
              placeholder="输入生成图片的提示词..."
            />
          </Form.Item>

          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="选择分类">
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
              <Option value="Halloween">Halloween</Option>
              <Option value="Christmas">Christmas</Option>
              <Option value="Festival">Festival</Option>
              <Option value="Portrait">Portrait</Option>
              <Option value="Style">Style</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>

          {/* 图片上传 */}
          <Form.Item label="预览图（必填）" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleUploadPreview}
              >
                <Button icon={<UploadOutlined />} loading={uploadingPreview}>
                  {uploadingPreview ? '上传中...' : '上传预览图'}
                </Button>
              </Upload>
              {previewImageUrl && (
                <div>
                  <img
                    src={previewImageUrl}
                    alt="预览图"
                    style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid var(--admin-border)', borderRadius: '4px' }}
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--admin-secondary)' }}>
                    {previewImageUrl}
                  </div>
                </div>
              )}
            </Space>
          </Form.Item>

          <Form.Item label="原图（可选）">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleUploadSource}
              >
                <Button icon={<UploadOutlined />} loading={uploadingSource}>
                  {uploadingSource ? '上传中...' : '上传原图'}
                </Button>
              </Upload>
              {sourceImageUrl && (
                <div>
                  <img
                    src={sourceImageUrl}
                    alt="原图"
                    style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid var(--admin-border)', borderRadius: '4px' }}
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--admin-secondary)' }}>
                    {sourceImageUrl}
                  </div>
                  <Button
                    size="small"
                    danger
                    onClick={() => setSourceImageUrl('')}
                    style={{ marginTop: '8px' }}
                  >
                    移除
                  </Button>
                </div>
              )}
            </Space>
          </Form.Item>

          {/* 元数据 */}
          <Form.Item
            label="目标受众"
            name="targetAudience"
            tooltip="选择该模板适合的人群"
          >
            <Select
              mode="multiple"
              placeholder="选择目标受众"
              options={[
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
                { label: 'Teen', value: 'teen' },
                { label: 'Kids', value: 'kids' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="标签"
            name="tags"
            tooltip="添加标签，如 trending、new、halloween 等"
          >
            <Select
              mode="tags"
              placeholder="输入标签（回车添加）"
              tokenSeparators={[',']}
            />
          </Form.Item>

          {/* 状态和排序 */}
          <Form.Item label="启用状态" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            label="排序"
            name="sortOrder"
            tooltip="数字越小越靠前"
          >
            <InputNumber min={0} style={{ width: '150px' }} />
          </Form.Item>

          {/* 提交按钮 */}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? '更新' : '创建'}
              </Button>
              <Button onClick={() => navigate('/templates')}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 响应式样式 */}
      <style>{`
        /* 小屏幕隐藏按钮文字 */}
        @media (max-width: 576px) {
          .btn-text {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default TemplateForm;
